/**
 * Editor enriquecido basado en TipTap.
 * - Guarda en Markdown (no HTML)
 * - Drag & drop + pegar imágenes (sube a R2 vía /api/uploads)
 * - Tablas, checklist, headings, callouts, code blocks, links
 */
import { useEffect, useMemo, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import {
  Bold,
  Italic,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListChecks,
  Code2,
  Quote,
  LinkIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Undo2,
  Redo2,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useUploadFile } from '@/features/uploads/queries';
import { htmlToMarkdown, markdownToHtmlBasic } from './markdown';
import { cn } from '@/lib/utils';

interface Props {
  value: string; // markdown
  onChange: (md: string) => void;
  articleId?: string;
  placeholder?: string;
  className?: string;
}

export function Editor({ value, onChange, articleId, placeholder, className }: Props) {
  const upload = useUploadFile(articleId);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const initialHtml = useMemo(() => markdownToHtmlBasic(value || ''), [value]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: { HTMLAttributes: { class: 'kb-code' } } }),
      Image.configure({ allowBase64: false, inline: false }),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder: placeholder ?? 'Empieza a escribir…' }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: initialHtml,
    editorProps: {
      attributes: {
        class: 'prose-kb focus:outline-none min-h-[280px]',
      },
      handlePaste(_view, event) {
        const items = Array.from(event.clipboardData?.items ?? []);
        const imageItem = items.find((i) => i.type.startsWith('image/'));
        if (imageItem) {
          const file = imageItem.getAsFile();
          if (file) {
            event.preventDefault();
            uploadAndInsert(file);
            return true;
          }
        }
        return false;
      },
      handleDrop(_view, event) {
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (files.length === 0) return false;
        event.preventDefault();
        for (const file of files) uploadAndInsert(file);
        return true;
      },
    },
    onUpdate({ editor }) {
      const html = editor.getHTML();
      const md = htmlToMarkdown(html);
      onChange(md);
    },
  });

  useEffect(() => {
    return () => editor?.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function uploadAndInsert(file: File) {
    setUploadError(null);
    try {
      const media = await upload.mutateAsync(file);
      if (media.tipo === 'image') {
        editor?.chain().focus().setImage({ src: media.url, alt: media.nombre }).run();
      } else {
        editor
          ?.chain()
          .focus()
          .insertContent(
            `<p><a href="${media.url}" target="_blank">📎 ${media.nombre}</a></p>`,
          )
          .run();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'No se pudo subir el archivo';
      setUploadError(`No se pudo subir "${file.name}": ${msg}`);
      console.error('Upload falló', e);
    }
  }

  if (!editor) return null;

  return (
    <div className={cn('bg-card rounded-lg border', className)}>
      <Toolbar editor={editor} onUpload={uploadAndInsert} uploading={upload.isPending} />
      {uploadError && (
        <div className="bg-destructive/10 text-destructive border-b px-4 py-2 text-sm">
          {uploadError}
          <button
            type="button"
            onClick={() => setUploadError(null)}
            className="ml-2 underline opacity-80 hover:opacity-100"
          >
            cerrar
          </button>
        </div>
      )}
      <div className="p-4">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({
  editor,
  onUpload,
  uploading,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  onUpload: (f: File) => void;
  uploading?: boolean;
}) {
  const btn = (active: boolean, onClick: () => void, Icon: typeof Bold, title: string) => (
    <Button
      type="button"
      variant={active ? 'secondary' : 'ghost'}
      size="icon"
      className="h-8 w-8"
      onClick={onClick}
      title={title}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );

  return (
    <div className="flex flex-wrap items-center gap-1 border-b p-1.5">
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), Bold, 'Negrita')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), Italic, 'Cursiva')}
      {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), Strikethrough, 'Tachado')}
      <Separator orientation="vertical" className="mx-1 h-5" />
      {btn(editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), Heading1, 'Título 1')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, 'Título 2')}
      {btn(editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, 'Título 3')}
      <Separator orientation="vertical" className="mx-1 h-5" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), List, 'Lista')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, 'Lista numerada')}
      {btn(editor.isActive('taskList'), () => editor.chain().focus().toggleTaskList().run(), ListChecks, 'Checklist')}
      {btn(editor.isActive('codeBlock'), () => editor.chain().focus().toggleCodeBlock().run(), Code2, 'Bloque de código')}
      {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), Quote, 'Cita / Callout')}
      <Separator orientation="vertical" className="mx-1 h-5" />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        title="Enlace"
        onClick={() => {
          const url = window.prompt('URL del enlace');
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }}
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        title="Insertar tabla"
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      >
        <TableIcon className="h-4 w-4" />
      </Button>
      <label
        className={cn(
          'hover:bg-accent inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2 text-xs font-medium',
          uploading && 'pointer-events-none opacity-60',
        )}
        title="Subir imagen o archivo"
      >
        {uploading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ImageIcon className="h-4 w-4" />
        )}
        <span>{uploading ? 'Subiendo…' : 'Subir'}</span>
        <input
          type="file"
          accept="image/*,application/pdf,.docx,.xlsx"
          className="hidden"
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(f);
            e.target.value = '';
          }}
        />
      </label>
      <Separator orientation="vertical" className="mx-1 h-5" />
      {btn(false, () => editor.chain().focus().undo().run(), Undo2, 'Deshacer')}
      {btn(false, () => editor.chain().focus().redo().run(), Redo2, 'Rehacer')}
    </div>
  );
}
